import { useEffect, useMemo, useState } from 'react';

export interface Cocktail {
  idDrink: string;
  strDrink: string;
  strDrinkThumb: string | null;
  strCategory: string | null;
  strAlcoholic: string | null;
  strGlass: string | null;
  strInstructions: string | null;
  isPopular?: boolean;
  [key: string]: any;
}

interface CocktailsResponse {
  letters: Record<string, { count: number; drinks: Cocktail[] }>;
  metadata?: Record<string, unknown>;
}

const DATA_URL = '/data/cocktails_A-Z.json';
const ALCOHOLIC_OPTIONS = ['Alcoholic', 'Non alcoholic', 'Optional alcohol'];
const INGREDIENT_OPTION_LIMIT = 30;
const INGREDIENT_FIELDS_TO_COUNT = 10;
const INGREDIENT_SKIP_LIST = [
  'ice',
  'water',
  'sugar',
  'powdered sugar',
  'carbonated water',
  'milk',
  'cream',
  'heavy cream',
];
const INGREDIENT_SKIP_SET = new Set(INGREDIENT_SKIP_LIST);

const normalizeIngredientName = (value: string) => value.trim().toLowerCase();
const formatIngredientLabel = (value: string) => {
  const trimmed = value.trim();
  return trimmed
    .toLowerCase()
    .replace(/(^|[\s-])(\S)/g, (_, boundary: string, char: string) => `${boundary}${char.toUpperCase()}`);
};

const buildIngredientFrequency = (drinks: Cocktail[], limit = INGREDIENT_OPTION_LIMIT) => {
  const counts = new Map<string, { label: string; count: number }>();
  drinks.forEach(drink => {
    for (let i = 1; i <= INGREDIENT_FIELDS_TO_COUNT; i += 1) {
      const value = drink[`strIngredient${i}`];
      if (value && typeof value === 'string') {
        const normalized = normalizeIngredientName(value);
        if (!normalized || INGREDIENT_SKIP_SET.has(normalized)) {
          continue;
        }
        const label = formatIngredientLabel(value);
        const current = counts.get(normalized);
        if (current) {
          current.count += 1;
        } else {
          counts.set(normalized, { label, count: 1 });
        }
      }
    }
  });
  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(entry => entry.label);
};

export const useCocktails = () => {
  const [drinks, setDrinks] = useState<Cocktail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await fetch(DATA_URL);
        if (!response.ok) {
          throw new Error(`Failed to load cocktails: ${response.status}`);
        }
        const data: CocktailsResponse = await response.json();
        if (!data.letters) {
          throw new Error('Malformed cocktail dataset');
        }
        const flattened = Object.values(data.letters)
          .flatMap(entry => entry.drinks)
          .filter(Boolean);
        if (isMounted) {
          setDrinks(flattened);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  const categories = useMemo(() => {
    const unique = new Set<string>();
    drinks.forEach(drink => {
      if (drink.strCategory) {
        unique.add(drink.strCategory);
      }
    });
    const sorted = Array.from(unique).sort((a, b) => a.localeCompare(b));
    return ['Popular', ...sorted];
  }, [drinks]);

  const ingredientOptions = useMemo(() => buildIngredientFrequency(drinks), [drinks]);

  return {
    drinks,
    loading,
    error,
    categories,
    ingredientOptions,
    alcoholicOptions: ALCOHOLIC_OPTIONS,
  } as const;
};

export default useCocktails;
